import { useEffect, useState, useMemo, useCallback, useRef, useContext } from "react";
import { Link } from "react-router-dom";
import GridLayout, { WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import {
  getDashboardStats,
  getUserPermissions,
  getModules,
  getDashboardLayout,
  saveDashboardLayout,
  resetDashboardLayout,
  runReport,
  getAssetUrl,
} from "../services/api";
import { useSystemConfig } from "../context/SystemConfigContext";
import LockedUsersWidget from "../components/UI/Widgets/LockedUsersWidget";
import LanguagePicker from "../components/UI/LanguagePicker";
import { notify } from "../utils/notify";
import { useTranslation, I18nContext } from "../context/I18nContext";

import {
  UsersIcon,
  UserGroupIcon,
  LockClosedIcon,
  ChevronRightIcon,
  ClockIcon,
  CheckCircleIcon,
  SignalIcon,
  Squares2X2Icon,
  ArrowPathIcon,
  CheckIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArchiveBoxIcon,
  ExclamationTriangleIcon,
  ReceiptRefundIcon,
  TicketIcon,
  GiftIcon,
  ReceiptPercentIcon,
  ScaleIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  ComputerDesktopIcon,
} from "@heroicons/react/24/outline";

import {
  PieChart, Pie, Cell, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, AreaChart, Area, Legend
} from 'recharts';

// A small fixed categorical palette reused across every report-chart
// widget's pie/bar segments below — kept separate from themeColor (which
// stays whatever the tenant's own brand color is) since a pie/bar chart
// needs several visually distinct colors, not one repeated brand color.
const CHART_PALETTE = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#06b6d4", "#8b5cf6", "#ec4899", "#84cc16"];

// today-N-days as "YYYY-MM-DD", matching what parseReportFilters
// (report_handler.go) expects for date_from/date_to — every widget below
// that windows its report to "the last N days" builds its date_from this
// way rather than leaving the report unfiltered, so a widget can't slowly
// balloon into summing a report's entire multi-year history.
function daysAgoISO(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

// Groups a report's rows by one column's value, summing another column,
// sorted descending by that sum — the one aggregation shape every
// per-transaction report widget below needs (Sales by Branch, Stock
// Movements by Type, Returns by Reason, Vouchers by Status, Loyalty by
// Type), so it's factored out once instead of five times.
function groupAndSum(rows, groupKey, sumKey) {
  const totals = new Map();
  for (const row of rows) {
    const label = row[groupKey] ?? "—";
    const value = Number(row[sumKey]) || 0;
    totals.set(label, (totals.get(label) || 0) + value);
  }
  return Array.from(totals, ([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
}

// Generic "fetch this report's rows once on mount" hook shared by every
// widget below — each widget only differs in which report key it asks
// for, what date window (if any) it applies, and how it shapes the
// resulting rows into a chart, all of which stay in the widget itself.
function useReportRows(token, reportKey, params) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);
  // params is a plain object literal at each call site (not memoized by the
  // caller), so it's serialized to gate the effect instead of listing it
  // directly — otherwise a new object identity on every render would
  // refetch in a loop.
  const paramsKey = JSON.stringify(params || {});
  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(false);
    runReport(token, reportKey, JSON.parse(paramsKey))
      .then((data) => { if (!cancelled) setRows(data?.rows || []); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, reportKey, paramsKey]);
  return { rows, error };
}

// Shared "loading / error / empty / chart" body so every widget below only
// has to supply the chart itself once data is actually in hand.
function ChartWidgetBody({ rows, error, empty, children }) {
  const { t } = useTranslation("dashboard");
  if (error) return <p className="text-sm text-gray-400 italic text-center py-6">{t("report_error", "Couldn't load this report")}</p>;
  if (rows === null) return <p className="text-sm text-gray-400 italic text-center py-6 animate-pulse">{t("report_loading", "Loading…")}</p>;
  if (empty) return <p className="text-sm text-gray-400 italic text-center py-6">{t("report_empty", "No data for this period")}</p>;
  return children;
}

// Same idea as groupAndSum, but counts rows per group instead of summing a
// money/number column — for reports where the group itself (movement
// type, voucher status) is the interesting signal, not a per-row amount.
function groupAndCount(rows, groupKey) {
  const totals = new Map();
  for (const row of rows) {
    const label = row[groupKey] ?? "—";
    totals.set(label, (totals.get(label) || 0) + 1);
  }
  return Array.from(totals, ([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
}

const CustomChartTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  // .label (a translated display label built by a widget from a raw
  // backend code — see enumLabel below) takes priority over .name/.period,
  // which stay the fallback for every widget whose group name is already
  // free text (a branch/product/customer name, a return reason, ...) and
  // never needed a translated label in the first place.
  return (
    <div className="bg-white/90 backdrop-blur-md border border-gray-100 p-3 shadow-xl rounded-2xl">
      <p className="text-xs font-medium text-gray-500 mb-1">{payload[0].payload.label || payload[0].payload.name || payload[0].payload.period}</p>
      <p className="text-lg font-bold" style={{ color: payload[0].payload.fill || payload[0].fill }}>
        {Number(payload[0].value).toLocaleString()}
      </p>
    </div>
  );
};

// Fixed backend enum/status codes that show up as a report's group name —
// see internal/models/loyalty.go and internal/models/inventory.go's Type
// doc comments, and vouchers_report.go's computed "status" text — mapped
// here to a translated display label instead of being shown as their raw
// code value (e.g. "earn_spend"), the same way every other piece of UI
// text on this page is translated.
const LOYALTY_TYPE_LABELS = {
  earn_spend: { key: "loyalty_type_earn_spend", en: "Earned (Spend)" },
  earn_product: { key: "loyalty_type_earn_product", en: "Earned (Product)" },
  redeem: { key: "loyalty_type_redeem", en: "Redeemed" },
  return_adjust: { key: "loyalty_type_return_adjust", en: "Return Adjustment" },
};
const STOCK_MOVEMENT_TYPE_LABELS = {
  purchase: { key: "stock_movement_purchase", en: "Purchase" },
  sale: { key: "stock_movement_sale", en: "Sale" },
  adjustment: { key: "stock_movement_adjustment", en: "Adjustment" },
  transfer_in: { key: "stock_movement_transfer_in", en: "Transfer In" },
  transfer_out: { key: "stock_movement_transfer_out", en: "Transfer Out" },
  return: { key: "stock_movement_return", en: "Return" },
};
const VOUCHER_STATUS_LABELS = {
  Active: { key: "voucher_status_active", en: "Active" },
  Exhausted: { key: "voucher_status_exhausted", en: "Exhausted" },
};

// Maps a raw enum/code value to its translated display label via one of
// the dictionaries above, falling back to the raw value itself for
// anything not in the dictionary — keeps a widget forward-compatible with
// a future backend enum member we haven't mapped here yet, rather than
// silently hiding it.
function enumLabel(t, dict, raw) {
  const entry = dict[raw];
  return entry ? t(entry.key, entry.en) : raw;
}

// --- One widget per registered report (see internal/reports/*_report.go
// and internal/config/seed_modules.go's "dashboard.report_*" entries) —
// each fetches that report's own rows via runReport and shapes them into a
// chart, rather than the app maintaining a second, separate aggregation
// pipeline just for the dashboard. ---

function SalesByBranchWidget({ token, themeColor }) {
  const { t } = useTranslation("dashboard");
  const { rows, error } = useReportRows(token, "sales", { date_from: daysAgoISO(30) });
  const data = useMemo(() => (rows ? groupAndSum(rows, "branch", "grand_total").slice(0, 8) : []), [rows]);
  return (
    <WidgetShell title={t("widget_report_sales", "Sales by Branch")} icon={BanknotesIcon} badge={<span className="text-[10px] text-gray-300">{t("badge_last_30_days", "Last 30 days")}</span>}>
      <div className="h-full min-h-[140px]">
        <ChartWidgetBody rows={rows} error={error} empty={data.length === 0}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <XAxis dataKey="name" axisLine={false} tickLine={false} fontSize={11} tick={{ fill: '#9ca3af' }} />
              <Tooltip content={<CustomChartTooltip />} cursor={{ fill: '#f3f4f6', radius: 8 }} wrapperStyle={{ outline: 'none' }} />
              <Bar dataKey="value" fill={themeColor} radius={[6, 6, 0, 0]} barSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </ChartWidgetBody>
      </div>
    </WidgetShell>
  );
}

function SalesTrendWidget({ token, themeColor }) {
  const { t } = useTranslation("dashboard");
  const { rows, error } = useReportRows(token, "sales_by_period", { date_from: daysAgoISO(30), period: "day" });
  const data = rows || [];
  return (
    <WidgetShell title={t("widget_report_sales_period", "Sales Trend")} icon={ArrowTrendingUpIcon} badge={<span className="text-[10px] text-gray-300">{t("badge_daily_last_30_days", "Daily, last 30 days")}</span>}>
      <div className="h-full min-h-[140px]">
        <ChartWidgetBody rows={rows} error={error} empty={data.length === 0}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <XAxis dataKey="period" axisLine={false} tickLine={false} fontSize={10} tick={{ fill: '#9ca3af' }} />
              <Tooltip formatter={(v) => Number(v).toLocaleString()} labelFormatter={(l) => l} />
              {/* name صراحة هنا — من غيره Recharts بيستخدم dataKey الخام
                  ("total_sales") كاسم افتراضي للسلسلة جوه الـ tooltip. */}
              <Area type="monotone" dataKey="total_sales" name={t("chart_total_sales_label", "Total Sales")} stroke={themeColor} strokeWidth={3} fill={themeColor} fillOpacity={0.15} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartWidgetBody>
      </div>
    </WidgetShell>
  );
}

function CashVarianceWidget({ token, themeColor }) {
  const { t } = useTranslation("dashboard");
  const { rows, error } = useReportRows(token, "shifts", { date_from: daysAgoISO(30) });
  const data = useMemo(() => {
    if (!rows) return [];
    // Only closed shifts actually carry a real variance — an open shift's
    // reconciliation hasn't happened yet, so charting it would just be a
    // row of zeroes.
    return rows
      .filter((r) => (r.status || "").toLowerCase() === "closed")
      .slice(-10)
      .map((r) => ({ name: `#${r.shift_number}`, value: Number(r.variance) || 0 }));
  }, [rows]);
  return (
    <WidgetShell title={t("widget_report_shifts", "Cash Variance by Shift")} icon={ScaleIcon} badge={<span className="text-[10px] text-gray-300">{t("badge_last_10_closed", "Last 10 closed")}</span>}>
      <div className="h-full min-h-[140px]">
        <ChartWidgetBody rows={rows} error={error} empty={data.length === 0}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <XAxis dataKey="name" axisLine={false} tickLine={false} fontSize={11} tick={{ fill: '#9ca3af' }} />
              <Tooltip content={<CustomChartTooltip />} cursor={{ fill: '#f3f4f6', radius: 8 }} wrapperStyle={{ outline: 'none' }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={28}>
                {data.map((entry, i) => (
                  <Cell key={i} fill={entry.value < 0 ? "#ef4444" : themeColor} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartWidgetBody>
      </div>
    </WidgetShell>
  );
}

function StockMovementsByTypeWidget({ token }) {
  const { t } = useTranslation("dashboard");
  const { rows, error } = useReportRows(token, "inventory", { date_from: daysAgoISO(30) });
  const data = useMemo(
    () => (rows ? groupAndCount(rows, "type").map((d) => ({ ...d, label: enumLabel(t, STOCK_MOVEMENT_TYPE_LABELS, d.name) })) : []),
    [rows, t]
  );
  return (
    <WidgetShell title={t("widget_report_inventory", "Stock Movements by Type")} icon={ArchiveBoxIcon} badge={<span className="text-[10px] text-gray-300">{t("badge_last_30_days", "Last 30 days")}</span>}>
      <div className="h-full min-h-[140px]">
        <ChartWidgetBody rows={rows} error={error} empty={data.length === 0}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} innerRadius={55} outerRadius={80} paddingAngle={6} dataKey="value" nameKey="label">
                {data.map((entry, i) => (
                  <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} strokeWidth={0} />
                ))}
              </Pie>
              <Tooltip content={<CustomChartTooltip />} />
              <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartWidgetBody>
      </div>
    </WidgetShell>
  );
}

function LowStockAlertWidget({ token, themeColor }) {
  const { t } = useTranslation("dashboard");
  // Recharts معندهوش دعم تلقائي لـ RTL — الشارت ده أفقي (layout="vertical"
  // في مصطلحات Recharts يعني الأعمدة بترسم أفقيًا) بمحور تصنيف (YAxis) على
  // الشمال والأعمدة بتكبر ناحية اليمين، وده لازم يتقلب يدويًا في العربي:
  // المحور ينقل لليمين والأعمدة تكبر ناحية الشمال، بدل ما يفضلوا ثابتين.
  const { languages, languageCode } = useContext(I18nContext);
  const isRTL = languages.find((l) => l.code === languageCode)?.direction === "rtl";
  const { rows, error } = useReportRows(token, "stock_levels", {});
  const data = useMemo(() => {
    if (!rows) return [];
    return [...rows]
      .sort((a, b) => (Number(a.available) || 0) - (Number(b.available) || 0))
      .slice(0, 8)
      .map((r) => ({ name: r.product, value: Number(r.available) || 0, low: (Number(r.available) || 0) <= (Number(r.reorder_point) || 0) }));
  }, [rows]);
  return (
    <WidgetShell title={t("widget_report_stock_levels", "Low Stock Alert")} icon={ExclamationTriangleIcon}>
      <div className="h-full min-h-[180px]">
        <ChartWidgetBody rows={rows} error={error} empty={data.length === 0}>
          {/* هذا الـ <div dir="ltr"> ضروري وليس زخرفة: إحنا بنقلب شكل
              الشارت يدويًا بنفسنا (orientation/reversed/radius تحت حسب
              isRTL) — لكن اتضح إن SVG's text-anchor="start" اللي Recharts
              بترسمه لأسماء المنتجات على المحور بيتفسر بشكل bidi-aware من
              المتصفح: لما يكون فيه ancestor بـ direction:rtl (زي الـ
              dir="rtl" اللي إحنا حاطينه على كل عنصر في شبكة الداشبورد)،
              "start" بتتفسر معكوسة (ترسو على اليمين وتمتد لليسار) فوق
              الأعمدة نفسها بدل ما تقف في مساحتها المخصصة يمين المحور —
              نفس فكرة مشكلة react-grid-layout اللي حصلت قبل كده (تعارض
              بين تعديلنا اليدوي واتجاه المتصفح التلقائي). تثبيت الاتجاه
              هنا على ltr بيمنع المتصفح من "يقلب" حاجة إحنا أصلاً قالبينها
              بنفسنا. (تم التأكد من السبب ده بمحاكاة فعلية للرندر). */}
          <div dir="ltr" className="w-full h-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} layout="vertical" margin={isRTL ? { right: 24 } : { left: 24 }}>
                <XAxis type="number" hide reversed={isRTL} />
                <YAxis
                  type="category"
                  dataKey="name"
                  orientation={isRTL ? "right" : "left"}
                  axisLine={false}
                  tickLine={false}
                  fontSize={11}
                  width={110}
                  tick={{ fill: '#9ca3af' }}
                />
                <Tooltip content={<CustomChartTooltip />} cursor={{ fill: '#f3f4f6' }} wrapperStyle={{ outline: 'none' }} />
                {/* radius ثابتة [0,6,6,0] في الحالتين عمدًا: لما اختبرتها
                    فعليًا اتضح إن Recharts بيحسب زوايا الاستدارة بالنسبة
                    لطرف القيمة (بعيد عن المحور) مش بالنسبة لركن الشاشة
                    الفيزيائي — فلما محور X بيبقى reversed (في RTL)،
                    نفس الـ array دي بترسم الاستدارة صح ناحية الطرف (الشمال
                    في RTL) تلقائيًا من غير ما نقلبها إحنا. قلبها يدويًا (زي
                    ما كان مكتوب قبل كده) كان بيرجّعها غلط لناحية اليمين. */}
                <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={16}>
                  {data.map((entry, i) => (
                    <Cell key={i} fill={entry.low ? "#ef4444" : themeColor} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartWidgetBody>
      </div>
    </WidgetShell>
  );
}

function ReturnsByReasonWidget({ token, themeColor }) {
  const { t } = useTranslation("dashboard");
  const { rows, error } = useReportRows(token, "returns", { date_from: daysAgoISO(30) });
  const data = useMemo(() => (rows ? groupAndSum(rows, "reason", "refund_amount").slice(0, 8) : []), [rows]);
  return (
    <WidgetShell title={t("widget_report_returns", "Returns by Reason")} icon={ReceiptRefundIcon} badge={<span className="text-[10px] text-gray-300">{t("badge_last_30_days", "Last 30 days")}</span>}>
      <div className="h-full min-h-[140px]">
        <ChartWidgetBody rows={rows} error={error} empty={data.length === 0}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <XAxis dataKey="name" axisLine={false} tickLine={false} fontSize={10} tick={{ fill: '#9ca3af' }} />
              <Tooltip content={<CustomChartTooltip />} cursor={{ fill: '#f3f4f6', radius: 8 }} wrapperStyle={{ outline: 'none' }} />
              <Bar dataKey="value" fill={themeColor} radius={[6, 6, 0, 0]} barSize={30} />
            </BarChart>
          </ResponsiveContainer>
        </ChartWidgetBody>
      </div>
    </WidgetShell>
  );
}

function VouchersByStatusWidget({ token }) {
  const { t } = useTranslation("dashboard");
  const { rows, error } = useReportRows(token, "vouchers", { date_from: daysAgoISO(90) });
  const data = useMemo(
    () => (rows ? groupAndCount(rows, "status").map((d) => ({ ...d, label: enumLabel(t, VOUCHER_STATUS_LABELS, d.name) })) : []),
    [rows, t]
  );
  return (
    <WidgetShell title={t("widget_report_vouchers", "Vouchers by Status")} icon={TicketIcon} badge={<span className="text-[10px] text-gray-300">{t("badge_last_90_days", "Last 90 days")}</span>}>
      <div className="h-full min-h-[140px]">
        <ChartWidgetBody rows={rows} error={error} empty={data.length === 0}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} innerRadius={55} outerRadius={80} paddingAngle={6} dataKey="value" nameKey="label">
                {data.map((entry, i) => (
                  <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} strokeWidth={0} />
                ))}
              </Pie>
              <Tooltip content={<CustomChartTooltip />} />
              <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartWidgetBody>
      </div>
    </WidgetShell>
  );
}

function LoyaltyPointsByTypeWidget({ token, themeColor }) {
  const { t } = useTranslation("dashboard");
  const { rows, error } = useReportRows(token, "loyalty", { date_from: daysAgoISO(30) });
  const data = useMemo(
    () => (rows ? groupAndSum(rows, "type", "points").map((d) => ({ ...d, label: enumLabel(t, LOYALTY_TYPE_LABELS, d.name) })) : []),
    [rows, t]
  );
  return (
    <WidgetShell title={t("widget_report_loyalty", "Loyalty Points by Type")} icon={GiftIcon} badge={<span className="text-[10px] text-gray-300">{t("badge_last_30_days", "Last 30 days")}</span>}>
      <div className="h-full min-h-[140px]">
        <ChartWidgetBody rows={rows} error={error} empty={data.length === 0}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <XAxis dataKey="label" axisLine={false} tickLine={false} fontSize={11} tick={{ fill: '#9ca3af' }} />
              <Tooltip content={<CustomChartTooltip />} cursor={{ fill: '#f3f4f6', radius: 8 }} wrapperStyle={{ outline: 'none' }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={36}>
                {data.map((entry, i) => (
                  <Cell key={i} fill={entry.value < 0 ? "#ef4444" : themeColor} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartWidgetBody>
      </div>
    </WidgetShell>
  );
}

function TaxCollectedWidget({ token }) {
  const { t } = useTranslation("dashboard");
  const { rows, error } = useReportRows(token, "taxes", { date_from: daysAgoISO(30) });
  const data = useMemo(
    () => (rows || []).map((r) => ({ name: r.tax_name, value: Number(r.tax_collected) || 0 })),
    [rows]
  );
  return (
    <WidgetShell title={t("widget_report_taxes", "Tax Collected by Type")} icon={ReceiptPercentIcon} badge={<span className="text-[10px] text-gray-300">{t("badge_last_30_days", "Last 30 days")}</span>}>
      <div className="h-full min-h-[140px]">
        <ChartWidgetBody rows={rows} error={error} empty={data.length === 0}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} innerRadius={55} outerRadius={80} paddingAngle={6} dataKey="value">
                {data.map((entry, i) => (
                  <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} strokeWidth={0} />
                ))}
              </Pie>
              <Tooltip content={<CustomChartTooltip />} />
              <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartWidgetBody>
      </div>
    </WidgetShell>
  );
}

const ReactGridLayout = WidthProvider(GridLayout);

const GRID_COLS = 12;
const ROW_HEIGHT = 20;

// Screen-size thresholds mirroring Tailwind's own default breakpoints
// (sm=640, lg=1024): under 640px counts as "mobile", 640–1023px as
// "tablet", 1024px and up as "desktop". Used only to pick which SAVED
// arrangement renders for a normal (non-editing) visit — while customizing,
// the admin's own tab selection decides instead (see editBreakpoint in
// Dashboard() below).
function getBreakpointForWidth(width) {
  if (width < 640) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

// Tracks the real, live viewport width and returns which of the three
// breakpoints it currently falls in, updating on resize — this is what lets
// simply opening the dashboard on a phone show that breakpoint's own saved
// arrangement automatically, without the visitor ever touching "Customize".
function useViewportBreakpoint() {
  const [breakpoint, setBreakpoint] = useState(() =>
    getBreakpointForWidth(typeof window !== "undefined" ? window.innerWidth : 1280)
  );
  useEffect(() => {
    const onResize = () => setBreakpoint(getBreakpointForWidth(window.innerWidth));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return breakpoint;
}

// The three customization tabs shown while editMode is on, in display
// order — each one edits a fully independent saved arrangement (see
// activeBreakpoint below and internal/handlers/layout_handler.go).
const BREAKPOINT_TABS = [
  { key: "mobile", icon: DevicePhoneMobileIcon, labelKey: "breakpoint_mobile", labelEn: "Mobile" },
  { key: "tablet", icon: DeviceTabletIcon, labelKey: "breakpoint_tablet", labelEn: "Tablet" },
  { key: "desktop", icon: ComputerDesktopIcon, labelKey: "breakpoint_desktop", labelEn: "Desktop" },
];

// Static assets (extension packages, uploaded avatars) are served from the
// server's bare origin ("/uploads/...", registered directly on the Fiber
// app), NOT under "/api" like every other endpoint on this page. Using
// REACT_APP_API_URL directly here would build "http://host:port/api/uploads/..."
// — a path that doesn't match the static route, and which (because it
// still starts with "/api/") gets caught by the JWT-protected route group
// and rejected with "Missing token" before ever reaching it. REACT_APP_URL
// is the bare origin env var meant for exactly this; if it's ever unset,
// fall back to stripping a trailing "/api" off REACT_APP_API_URL.
const STATIC_ORIGIN = process.env.REACT_APP_URL || (process.env.REACT_APP_API_URL || "").replace(/\/api\/?$/, "");

// The fixed order/registry of every built-in dashboard widget. Adding a new
// built-in widget later means: register it as a Module (seed_modules.go),
// add it here with a default height, and add a case for it in
// renderModuleBody below.
const MODULE_ORDER = [
  "dashboard.network_performance",
  "dashboard.user_status",
  "dashboard.operational_stats",
  "dashboard.recent_activity",
  "dashboard.quick_action_users",
  "dashboard.quick_action_permissions",
  "dashboard.quick_action_events",
  "dashboard.locked_users_widget",
  "dashboard.report_sales",
  "dashboard.report_sales_by_period",
  "dashboard.report_shifts",
  "dashboard.report_inventory",
  "dashboard.report_stock_levels",
  "dashboard.report_returns",
  "dashboard.report_vouchers",
  "dashboard.report_loyalty",
  "dashboard.report_taxes",
];

// Default height (in grid row units) for a widget the user hasn't resized
// yet — only used the first time a widget appears in someone's personal
// layout. Purely a starting point; every widget is freely resizable after.
const DEFAULT_HEIGHTS = {
  "dashboard.network_performance": 11,
  "dashboard.user_status": 13,
  "dashboard.operational_stats": 13,
  "dashboard.recent_activity": 13,
  "dashboard.quick_action_users": 5,
  "dashboard.quick_action_permissions": 5,
  "dashboard.quick_action_events": 5,
  "dashboard.locked_users_widget": 11,
  "dashboard.report_sales": 11,
  "dashboard.report_sales_by_period": 11,
  "dashboard.report_shifts": 11,
  "dashboard.report_inventory": 13,
  "dashboard.report_stock_levels": 14,
  "dashboard.report_returns": 11,
  "dashboard.report_vouchers": 13,
  "dashboard.report_loyalty": 11,
  "dashboard.report_taxes": 13,
};
const DEFAULT_EXTENSION_HEIGHT = 12;

// Packs a list of {w, h, ...} specs left-to-right, wrapping at GRID_COLS,
// starting from a given y — used to build a brand-new user's default
// layout and to place any newly-visible widget at the bottom of whatever
// they've already arranged.
function packItems(specs, startY = 0) {
  let cursorX = 0, cursorY = startY, rowMaxH = 0;
  const placed = [];
  for (const spec of specs) {
    const w = Math.max(1, Math.min(spec.w, GRID_COLS));
    if (cursorX + w > GRID_COLS) {
      cursorY += rowMaxH;
      cursorX = 0;
      rowMaxH = 0;
    }
    placed.push({ ...spec, x: cursorX, y: cursorY, w });
    cursorX += w;
    rowMaxH = Math.max(rowMaxH, spec.h);
  }
  return placed;
}

// A generic card shell used for widgets that don't already bring their own
// full card chrome.
function WidgetShell({ title, icon: Icon, badge, children }) {
  return (
    <div className="h-full w-full bg-white rounded-3xl border border-gray-100 shadow-sm flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-6 pt-5 pb-3 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          {Icon && <Icon className="w-5 h-5 text-gray-400 shrink-0" />}
          <h3 className="font-bold text-gray-800 text-sm tracking-tight truncate">{title}</h3>
        </div>
        {badge}
      </div>
      <div className="flex-1 min-h-0 overflow-auto px-6 pb-5">{children}</div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation("dashboard");
  // Installed (non-builtin) extensions get their own host-rendered title
  // translated through this separate "modules" namespace — see
  // module_handler.go's InstallModuleHandler, which auto-registers/updates
  // a "modules"/<module key> translation key on every (re)install so an
  // admin can add a title translation from the Translations screen without
  // touching the extension's own package at all.
  const { t: tModules } = useTranslation("modules");
  // The saved x/y/w/h in layoutItems always live in one fixed (packItems'
  // own left-to-right) coordinate frame, regardless of which language the
  // user was viewing when they last arranged things — react-grid-layout
  // positions items with a physical inline transform that never mirrors on
  // its own for an RTL ancestor (only flexbox/grid text flow does), so
  // without this the widget grid would keep rendering "as if still LTR"
  // while everything else on the page (hero text, toolbar, buttons) mirrors
  // around it. isRTL below is used purely at render/interaction time to
  // mirror x for display (topLayout) and un-mirror it back before it's
  // written into state (handleTopLayoutChange) — the stored layout itself
  // never changes shape just because the viewing language changed.
  const { languages, languageCode } = useContext(I18nContext);
  const isRTL = languages.find((l) => l.code === languageCode)?.direction === "rtl";
  const { systemSettings } = useSystemConfig();
  const [stats, setStats] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  const [layoutItems, setLayoutItems] = useState([]);
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [savingLayout, setSavingLayout] = useState(false);
  // Real screen width, tracked live — this (not editBreakpoint below) is
  // what picks which saved arrangement renders whenever we're NOT actively
  // customizing, so simply opening the dashboard on a phone shows that
  // breakpoint's own arrangement instead of always defaulting to desktop.
  const viewportBreakpoint = useViewportBreakpoint();
  // Which of the three tabs the admin is arranging right now — only
  // consulted while editMode is true. Starts out matching the real
  // viewport so clicking "Customize Layout" opens on the arrangement for
  // whatever screen you're actually looking at (see handleToggleEditMode,
  // which re-syncs this every time edit mode is entered).
  const [editBreakpoint, setEditBreakpoint] = useState(viewportBreakpoint);
  const activeBreakpoint = editMode ? editBreakpoint : viewportBreakpoint;
  // True once a drag/resize has actually changed something in the current
  // tab since it was last loaded or saved — guards handleBreakpointTabChange
  // and handleToggleEditMode below from silently discarding an in-progress
  // edit when switching tabs or leaving customize mode.
  const [layoutDirty, setLayoutDirty] = useState(false);

  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("userProfile"));
  const themeColor = systemSettings?.appearance?.theme_color || "#203664";
  const lockedWidgetRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [perms, data, mods] = await Promise.all([
        getUserPermissions(token),
        getDashboardStats(token, languageCode),
        getModules(token),
      ]);
      const permStrings = perms.map(p => typeof p === 'object' ? p.event_name : p);
      setPermissions(permStrings);
      setStats(data);
      setModules(mods || []);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [token, languageCode]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // The saved personal layout is fetched separately from the 30s
  // stats/modules poll — editing shouldn't get clobbered by a background
  // refresh — and re-fetched every time activeBreakpoint changes: either
  // because the admin switched customization tabs, or because the real
  // viewport crossed a breakpoint threshold (e.g. the browser window was
  // resized) while not editing.
  useEffect(() => {
    let cancelled = false;
    setLayoutLoaded(false);
    (async () => {
      try {
        const res = await getDashboardLayout(token, activeBreakpoint);
        if (cancelled) return;
        setLayoutItems(res?.items || []);
        setLayoutDirty(false);
      } catch (err) {
        console.error("Failed to load dashboard layout:", err);
      } finally {
        if (!cancelled) setLayoutLoaded(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, activeBreakpoint]);

  // Every dashboard section is registered in the generic Module registry
  // (see internal/config/seed_modules.go) with its own dedicated permission.
  // Two independent gates decide whether a section shows: the module must
  // be globally enabled (toggled from the /modules admin page) AND the
  // current user must hold that module's own permission (granted through
  // the existing Groups/Permissions pages).
  const modulesByKey = useMemo(() => {
    const map = {};
    (modules || []).forEach((m) => { map[m.key] = m; });
    return map;
  }, [modules]);

  const isModuleVisible = useCallback((key) => {
    const m = modulesByKey[key];
    if (!m) return false;
    return m.is_enabled && permissions.includes(m.permission);
  }, [modulesByKey, permissions]);

  const visibleModuleKeys = useMemo(() => {
    const builtinVisible = MODULE_ORDER.filter((k) => isModuleVisible(k));
    const extensionVisible = (modules || [])
      .filter((m) => m.category === "dashboard" && !m.is_builtin && m.is_enabled && permissions.includes(m.permission))
      .map((m) => m.key);
    return [...builtinVisible, ...extensionVisible];
  }, [modules, permissions, isModuleVisible]);

  // Hero section gates, each tied to its own permission rather than shown
  // to everyone: the security status line (System Healthy / Security
  // Attention Required) needs the same permission that gates the locked-
  // users widget itself — dashboard_locked_users_widget_view, the actual
  // "can see and unblock locked accounts" permission (see
  // seed_modules.go's "dashboard.locked_users_widget" entry) — since
  // there's no point telling someone the system's lock status if they
  // can't act on it. View Reports uses the exact same "at least one
  // report_view_* permission" rule MainLayout.js's own sidebar already
  // uses for its Reports nav item (there's no single "reports" permission
  // — access is per-report). System Logs mirrors App.js's own route gate
  // for "/logs" (requiredPermission="activity_log_view").
  const canSeeSecurityStatus = isModuleVisible("dashboard.locked_users_widget");
  const canViewReports = useMemo(
    () => permissions.some((p) => p.startsWith("report_view_")),
    [permissions]
  );
  const canViewLogs = permissions.includes("activity_log_view");

  // Reconciles the personal layout with what's actually visible right now:
  // fills in a sensible default position/size for any widget the user has
  // never positioned before (this covers both "brand-new user, nothing
  // saved yet" and "an admin just enabled/installed something new"),
  // without touching anything the user has already arranged.
  useEffect(() => {
    if (!layoutLoaded) return;
    setLayoutItems((prev) => {
      const existingKeys = new Set(prev.map((i) => i.item_key));
      const missing = visibleModuleKeys.filter((k) => !existingKeys.has(k));
      if (missing.length === 0) return prev;
      const maxY = prev.reduce((m, i) => (i.parent_key ? m : Math.max(m, i.y + i.h)), 0);
      const specs = missing.map((key) => {
        const mod = modulesByKey[key];
        const isBuiltinKey = MODULE_ORDER.includes(key);
        return {
          item_key: key,
          item_type: "module",
          parent_key: "",
          title: "",
          w: mod?.width || 4,
          h: isBuiltinKey ? (DEFAULT_HEIGHTS[key] || 8) : DEFAULT_EXTENSION_HEIGHT,
        };
      });
      return [...prev, ...packItems(specs, maxY)];
    });
  }, [layoutLoaded, visibleModuleKeys, modulesByKey]);

  // A widget from an earlier saved layout that no longer resolves to
  // anything visible right now is simply not rendered.
  const isItemVisible = useCallback(
    (item) => visibleModuleKeys.includes(item.item_key),
    [visibleModuleKeys]
  );

  // Every item lives in one flat personal grid — no grouping/containers.
  const topLevelItems = useMemo(
    () => layoutItems.filter((i) => i.item_type === "module" && isItemVisible(i)),
    [layoutItems, isItemVisible]
  );

  const handleTopLayoutChange = (newLayout) => {
    if (!editMode) return;
    setLayoutItems((prev) => prev.map((item) => {
      const match = newLayout.find((l) => l.i === item.item_key);
      if (!match) return item;
      // newLayout's x is in "visual" (possibly mirrored) space — see
      // isRTL's own doc comment above — so it's converted straight back to
      // the canonical frame before it ever lands in state/gets saved.
      const x = isRTL ? GRID_COLS - match.w - match.x : match.x;
      // react-grid-layout fires onLayoutChange on mount/prop updates too,
      // not just on an actual drag/resize — comparing against the item's
      // current values before flagging layoutDirty keeps that noise from
      // triggering an "unsaved changes" prompt nobody actually earned.
      if (x === item.x && match.y === item.y && match.w === item.w && match.h === item.h) {
        return item;
      }
      setLayoutDirty(true);
      return { ...item, x, y: match.y, w: match.w, h: match.h };
    }));
  };

  // Shared guard for anything that would throw away the current tab's
  // in-progress edits (switching breakpoint tabs, leaving customize mode):
  // a no-op when nothing's actually changed, otherwise a confirm prompt.
  const confirmDiscardIfDirty = () => {
    if (!layoutDirty) return true;
    return window.confirm(t("unsaved_layout_changes_confirm", "You have unsaved changes. Discard them and continue?"));
  };

  const handleBreakpointTabChange = (key) => {
    if (key === editBreakpoint) return;
    if (!confirmDiscardIfDirty()) return;
    setEditBreakpoint(key);
  };

  const handleToggleEditMode = () => {
    if (editMode && !confirmDiscardIfDirty()) return;
    setEditMode((v) => {
      const entering = !v;
      // Re-sync to whatever the real viewport is right now every time
      // customize mode is (re-)entered, in case the browser was resized
      // since editBreakpoint was last set.
      if (entering) setEditBreakpoint(viewportBreakpoint);
      return entering;
    });
  };

  const handleSaveLayout = async () => {
    setSavingLayout(true);
    try {
      const items = layoutItems.map(({ item_key, item_type, parent_key, title, x, y, w, h }) => ({
        item_key, item_type, parent_key: parent_key || "", title: title || "", x, y, w, h,
      }));
      await saveDashboardLayout(token, { items }, activeBreakpoint);
      setLayoutDirty(false);
      notify.success(t("notify_layout_saved", "Dashboard layout saved"));
    } catch (err) {
      notify.error(err.message || t("notify_layout_save_failed", "Failed to save layout"));
    } finally {
      setSavingLayout(false);
    }
  };

  const handleResetLayout = async () => {
    try {
      await resetDashboardLayout(token, activeBreakpoint);
      const specs = visibleModuleKeys.map((key) => {
        const mod = modulesByKey[key];
        const isBuiltinKey = MODULE_ORDER.includes(key);
        return {
          item_key: key, item_type: "module", parent_key: "", title: "",
          w: mod?.width || 4,
          h: isBuiltinKey ? (DEFAULT_HEIGHTS[key] || 8) : DEFAULT_EXTENSION_HEIGHT,
        };
      });
      setLayoutItems(packItems(specs, 0));
      setLayoutDirty(false);
      notify.success(t("notify_layout_reset", "Layout reset to default"));
    } catch (err) {
      notify.error(err.message || t("notify_layout_reset_failed", "Failed to reset layout"));
    }
  };

  const otherActiveUsers = useMemo(() => {
    if (!stats?.recent_activity) return [];
    const uniqueUsers = new Map();
    stats.recent_activity.forEach(act => {
      if (
        act.user &&
        act.user.id !== currentUser?.id &&
        act.event_name !== "USER_LOGOUT"
      ) {
        uniqueUsers.set(act.user.id, act.user);
      }
    });
    return Array.from(uniqueUsers.values()).slice(0, 3);
  }, [stats, currentUser]);

  const adjustedOnlineCount = Math.max(0, (stats?.active_today || 0) - 1);

  const userStats = useMemo(() => {
    if (!stats) return [];
    const active = stats.active_today || 0;
    const banned = stats.banned_users || 0;
    const total = stats.total_users || 0;
    const inactive = stats.InactiveCount || 0;
    const recentlyOut = Math.max(0, total - (active + banned + inactive));
    const allStats = [
      { name: 'Active', label: t("user_status_active", "Active"), value: active, color: '#10b981' },
      { name: 'Inactive', label: t("user_status_inactive", "Inactive"), value: inactive, color: '#f59e0b' },
      { name: 'Banned', label: t("user_status_banned", "Banned"), value: banned, color: '#ef4444' },
      { name: 'Recently Out', label: t("user_status_recently_out", "Recently Out"), value: recentlyOut, color: '#9ca3af' }
    ];
    return allStats.filter(stat => stat.value > 0);
  }, [stats, t]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white/90 backdrop-blur-md border border-gray-100 p-3 shadow-xl rounded-2xl">
          <p className="text-xs font-medium text-gray-500 mb-1">{payload[0].payload.n}</p>
          <p className="text-lg font-bold" style={{ color: payload[0].fill }}>
            {payload[0].value.toLocaleString()}
            <span className="text-[10px] text-gray-400 ms-1 font-normal">{t("chart_entries_label", "Entries")}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Renders the actual content for one item, given its key. Builtin widget
  // bodies are switched on their module key; anything else is assumed to
  // be an installed dashboard-category extension, rendered as a sandboxed
  // iframe (see internal/handlers/module_handler.go for the install/
  // security notes — it never runs as trusted code inside this page).
  const renderItem = (item) => {
    switch (item.item_key) {
      case "dashboard.network_performance":
        return (
          <div className="h-full w-full bg-gray-900 rounded-3xl p-6 text-white shadow-xl relative overflow-hidden flex flex-col">
            <div className="flex justify-between items-center relative z-10 text-start shrink-0">
              <div>
                <h3 className="text-lg font-bold">{t("widget_network_performance", "Network Performance")}</h3>
                <p className="text-xs text-gray-400">{t("widget_network_performance_subtitle", "Activity Load")}</p>
              </div>
              <div className="flex items-center gap-2 bg-white/5 px-3 py-1 rounded-full border border-white/10 shrink-0">
                <CheckCircleIcon className="w-4 h-4 text-green-400" />
                <span className="text-xs font-medium">{t("widget_optimal_badge", "Optimal")}</span>
              </div>
            </div>
            <div className="flex-1 min-h-0 mt-6">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={[{ v: 30 }, { v: 55 }, { v: 45 }, { v: stats?.total_logs || 20 }, { v: 70 }, { v: 100 }]}>
                  <Area type="monotone" dataKey="v" stroke={themeColor} strokeWidth={4} fill={themeColor} fillOpacity={0.2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        );

      case "dashboard.user_status":
        return (
          <WidgetShell title={t("widget_user_status", "User Status")} icon={UsersIcon}>
            <div className="h-full min-h-[140px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={userStats} innerRadius={65} outerRadius={85} paddingAngle={8} dataKey="value" nameKey="label">
                    {userStats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} strokeWidth={0} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </WidgetShell>
        );

      case "dashboard.operational_stats":
        return (
          <WidgetShell
            title={t("widget_operational_stats", "Operational Stats")}
            icon={UserGroupIcon}
          >
            {/* Recharts has no built-in RTL support (see LowStockAlertWidget's
                own note above) — a plain BarChart like this one always draws
                its columns in array order, left to right, regardless of
                document direction. In Arabic that reads backwards (the
                first stat, "Logs", ends up on the visual left where a
                right-to-left reader expects the LAST item), so we reverse
                the column order ourselves when isRTL. The dir="ltr" wrapper
                then stops the browser from ALSO auto-mirroring this
                already-manually-mirrored chart (the same double-flip risk
                called out for the grid layout and the low-stock chart). */}
            <div className="h-full min-h-[140px]" dir="ltr">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={(() => {
                  const d = [
                    { n: t("operational_stats_logs", "Logs"), c: stats?.total_logs || 0 },
                    { n: t("operational_stats_active", "Active"), c: stats?.active_today || 0 },
                    { n: t("operational_stats_inactive", "Inactive"), c: userStats.find(s => s.name === 'Inactive')?.value || 0 }
                  ];
                  return isRTL ? d.reverse() : d;
                })()}>
                  <XAxis dataKey="n" axisLine={false} tickLine={false} fontSize={12} tick={{ fill: '#9ca3af' }} />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: '#f3f4f6', radius: 8 }}
                    wrapperStyle={{ outline: 'none' }}
                  />
                  <Bar dataKey="c" fill={themeColor} radius={[6, 6, 0, 0]} barSize={45} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </WidgetShell>
        );

      case "dashboard.recent_activity":
        return (
          <WidgetShell title={t("widget_recent_activity", "Recent Activity")} icon={ClockIcon}>
            {/* الخط الرأسي والنقاط بتاعة الـ Timeline: ps-8/start-0 بدل
                pl-8/left-0 عشان يفضلوا ملاصقين لبداية السطر (يتقلبوا تلقائيًا
                مع اتجاه اللغة)، و rtl:translate-x-px لأن -translate-x-px
                بتاعة تمركز الخط الرأسي فيزيائية (متأثرة باتجاه الشاشة الفعلي
                مش المنطقي) فلازم تتعكس يدويًا في العربي. */}
            <div className="space-y-8 relative before:absolute before:inset-0 before:ms-2.5 before:-translate-x-px rtl:before:translate-x-px before:h-full before:w-0.5 before:bg-gray-100">
              {stats?.recent_activity?.slice(0, 5).map((act) => (
                <div key={act.id} className="relative flex items-start gap-6 ps-8 group">
                  <div className="absolute start-0 mt-1 h-5 w-5 rounded-full bg-white border-2 border-gray-200 z-10 flex items-center justify-center">
                    <div className="h-1.5 w-1.5 rounded-full bg-gray-300"></div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-700 leading-none"><span className="font-bold">{act.user?.username}</span></p>
                    <p className="text-xs text-gray-500 mt-1">{act.event_name}</p>
                    <p className="text-[10px] text-gray-400 mt-1 italic">{new Date(act.created_at).toLocaleTimeString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </WidgetShell>
        );

      case "dashboard.quick_action_users":
        return (
          <WidgetShell title={t("widget_quick_users", "Users List")} icon={UsersIcon}>
            <Link to="/users" className="flex items-center justify-between h-full group">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600 group-hover:scale-110 transition-transform">
                  <UsersIcon className="w-6 h-6" />
                </div>
                <p className="text-xs text-gray-500">{t("widget_quick_users_desc", "View accounts")}</p>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-gray-300 group-hover:text-emerald-500 rtl:rotate-180" />
            </Link>
          </WidgetShell>
        );

      case "dashboard.quick_action_permissions":
        return (
          <WidgetShell title={t("widget_quick_permissions", "Security Roles")} icon={LockClosedIcon}>
            <Link to="/permissions" className="flex items-center justify-between h-full group">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-blue-50 text-blue-500 group-hover:scale-110 transition-transform">
                  <LockClosedIcon className="w-6 h-6" />
                </div>
                <p className="text-xs text-gray-500">{t("widget_quick_permissions_desc", "Manage access")}</p>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-gray-300 group-hover:text-blue-500 rtl:rotate-180" />
            </Link>
          </WidgetShell>
        );

      case "dashboard.quick_action_events":
        return (
          <WidgetShell title={t("widget_quick_events", "Monitored Events")} icon={SignalIcon}>
            <Link to="/events" className="flex items-center justify-between h-full group">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-purple-50 text-purple-600 group-hover:scale-110 transition-transform">
                  <SignalIcon className="w-6 h-6" />
                </div>
                <p className="text-xs text-gray-500">{t("widget_quick_events_desc", "Configure tracked events")}</p>
              </div>
              <ChevronRightIcon className="w-5 h-5 text-gray-300 group-hover:text-purple-500 rtl:rotate-180" />
            </Link>
          </WidgetShell>
        );

      case "dashboard.locked_users_widget":
        return (
          <div ref={lockedWidgetRef} className="h-full w-full flex flex-col scroll-mt-10">
            <div className="flex-1 min-h-0 overflow-auto">
              <LockedUsersWidget
                users={stats?.locked_users_list}
                token={token}
                onRefresh={fetchData}
              />
            </div>
          </div>
        );

      case "dashboard.report_sales":
        return <SalesByBranchWidget token={token} themeColor={themeColor} />;

      case "dashboard.report_sales_by_period":
        return <SalesTrendWidget token={token} themeColor={themeColor} />;

      case "dashboard.report_shifts":
        return <CashVarianceWidget token={token} themeColor={themeColor} />;

      case "dashboard.report_inventory":
        return <StockMovementsByTypeWidget token={token} />;

      case "dashboard.report_stock_levels":
        return <LowStockAlertWidget token={token} themeColor={themeColor} />;

      case "dashboard.report_returns":
        return <ReturnsByReasonWidget token={token} themeColor={themeColor} />;

      case "dashboard.report_vouchers":
        return <VouchersByStatusWidget token={token} />;

      case "dashboard.report_loyalty":
        return <LoyaltyPointsByTypeWidget token={token} themeColor={themeColor} />;

      case "dashboard.report_taxes":
        return <TaxCollectedWidget token={token} />;

      default: {
        // Any installed (non built-in) "dashboard" category extension.
        const mod = modulesByKey[item.item_key];
        if (!mod) return null;
        return (
          <div className="h-full w-full bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50 shrink-0">
              <h3 className="font-bold text-gray-800 text-sm truncate">{tModules(mod.key, mod.name)}</h3>
              <span className="text-[9px] font-black text-purple-500 bg-purple-50 px-2 py-0.5 rounded shrink-0">{t("extension_badge", "EXTENSION")}</span>
            </div>
            {/* The current JWT (and theme color, so a widget can match the
                app's branding) is passed via a URL fragment, never a query
                string: fragments are never sent over the wire to any
                server (ours or otherwise), so this doesn't leak the token
                into access logs — it only becomes readable to the
                extension's own JS via location.hash, letting a widget that
                needs real data call our API as this user (e.g.
                Authorization: Bearer <token>) without us having to expose
                a whole new unauthenticated endpoint just for widgets. An
                extension already runs arbitrary JS in this browser once
                installed (see module_handler.go's install security note),
                so handing it the same token it could already act with is
                not a new privilege — it's just made explicit.

                lang is the same idea, for translation instead of auth: the
                current system language code (e.g. "ar"), so an extension
                can fetch its own bundled "lang/<code>.json" (packaged
                inside its .zip alongside index.html, served statically at
                /uploads/modules/<key>/lang/<code>.json — no new backend
                endpoint needed, same as every other file in the package)
                and translate its own UI without the host having to know
                anything about the extension's text. See
                docs/extension-development.md for the full convention and
                a worked example. */}
            <iframe
              title={tModules(mod.key, mod.name)}
              src={`${STATIC_ORIGIN}${mod.entry_path}#token=${encodeURIComponent(token || "")}&theme=${encodeURIComponent(themeColor)}&lang=${encodeURIComponent(languageCode || "en")}`}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              className="w-full flex-1 border-0"
            />
          </div>
        );
      }
    }
  };

  if (loading) return <div className="p-10 text-center animate-pulse text-gray-400">{t("loading_text", "Loading Dashboard Data...")}</div>;

  // Mirrored to visual space for RTL — see isRTL's own doc comment above.
  const topLayout = topLevelItems.map((i) => ({
    i: i.item_key,
    x: isRTL ? GRID_COLS - i.w - i.x : i.x,
    y: i.y,
    w: i.w,
    h: i.h,
  }));


  return (
    <div className="space-y-6 pb-10">

      {/* 1. Hero Welcome Section */}
      <div className="relative overflow-hidden bg-white rounded-3xl border border-gray-100 shadow-sm p-8">
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-center md:text-start">
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">
              {t("welcome_prefix", "Welcome back,")} <span style={{ color: themeColor }}>{currentUser?.username || "User"}</span>!
            </h1>
            {(() => {
              const securityAlerts = stats?.locked_users_list?.length || 0;
              return (
                <div className="mt-2 space-y-1">
                  {/* Only shown to someone who can actually act on it — see
                      canSeeSecurityStatus's own doc comment above. */}
                  {canSeeSecurityStatus && (
                    <div
                      className={`flex items-center gap-2 text-sm font-medium ${securityAlerts > 0 ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                      onClick={() => securityAlerts > 0 && lockedWidgetRef.current?.scrollIntoView({ behavior: 'smooth' })}
                    >
                      {securityAlerts > 0 ? (
                        <>
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                          </span>
                          <span className="text-red-600 font-bold">{t("security_alert_label", "Security Attention Required:")}</span>
                          <span className="text-gray-500 font-normal underline decoration-red-200 underline-offset-4">
                            {securityAlerts} {t("security_alert_suffix", "account(s) temporarily locked.")}
                          </span>
                        </>
                      ) : (
                        <>
                          <div className="h-2 w-2 rounded-full bg-green-500"></div>
                          <span className="text-green-600 font-bold">{t("system_healthy_label", "System Healthy:")}</span>
                          <span className="text-gray-500 font-normal">{t("system_healthy_text", "No security threats detected.")}</span>
                        </>
                      )}
                    </div>
                  )}
                  <p className="text-gray-400 text-xs">
                    {t("you_have_prefix", "You have")} <span className="font-bold text-gray-600">{adjustedOnlineCount}</span> {t("active_members_text", "team member(s) active right now.")}
                  </p>
                </div>
              );
            })()}
            <div className="flex gap-3 mt-6 justify-center md:justify-start">
              {canViewReports && (
                <Link to="/reports" className="px-4 py-2 rounded-xl text-white text-sm font-medium shadow-lg transition-transform hover:scale-105 inline-block" style={{ backgroundColor: themeColor }}>
                  {t("view_reports_button", "View Reports")}
                </Link>
              )}
              {canViewLogs && (
                <Link to="/logs" className="px-4 py-2 rounded-xl bg-gray-50 text-gray-600 text-sm font-medium hover:bg-gray-100 transition-colors">
                  {t("system_logs_link", "System Logs")}
                </Link>
              )}
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            {/* rtl:space-x-reverse: -space-x-3 بتستخدم margin-left/margin-right
                فيزيائيًا (تايلويند نفسه مبينش نسخة منطقية ليها)، فبدون كده
                اتجاه التراكب بين الصور هيفضل ثابت غلط في العربي — دي الطريقة
                الرسمية اللي تايلويند بتنصح بيها لدعم RTL مع space-x. */}
            <div className="flex -space-x-3 rtl:space-x-reverse">
              {otherActiveUsers.map((u) => (
                <div key={u.id} className="h-12 w-12 rounded-full border-4 border-white bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-600 shadow-sm relative overflow-hidden">
                  {u.avatar_url ? (
                    <img src={getAssetUrl(u.avatar_url)} alt="avatar" className="w-full h-full object-cover" />
                  ) : (
                    u.username?.substring(0, 2).toUpperCase()
                  )}
                  <span className="absolute bottom-1 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                </div>
              ))}
            </div>
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1">
              <SignalIcon className="w-4 h-4 text-green-500" /> {t("live_presence_label", "Live Presence")}
            </span>
          </div>
        </div>
        {/* end-0 بدل right-0 عشان الدائرة الزخرفية تفضل في الركن الفاضي
            الصح (اللي بيتقلب مع المحتوى)، مش ثابتة على اليمين دايمًا —
            كانت بتتراكب على نص الترحيب لما يتقلب المحتوى للعربي. و
            rtl:-translate-x-1/4 لأن الـ translate فيزيائي دايمًا (متأثر
            باتجاه الشاشة الفعلي مش المنطقي) فلازم يتعكس يدويًا. */}
        <div className="absolute top-0 end-0 -translate-y-1/2 translate-x-1/4 rtl:-translate-x-1/4 w-64 h-64 rounded-full opacity-5" style={{ backgroundColor: themeColor }}></div>
      </div>

      {/* هذا الـ <div dir="ltr"> هو الإصلاح الحقيقي لمشكلة ترتيب عناصر
          الداشبورد في العربي: كل عنصر في الشبكة موضعه عن طريق
          transform: translate() بس من غير ما يحدد left/right صراحة، وده
          خلّى المتصفح — لما الصفحة كلها dir="rtl" — يحسب "نقطة البداية"
          الافتراضية للعنصر على إنها ملاصقة للحافة اليمنى (مش الشمال زي
          العادة)، وبعدين الـ transform بتاعنا (المحسوب صح فعلاً كما تأكدنا
          بالأرقام الفعلية من المتصفح) كان بيتضاف فوق الإزاحة دي بدل ما
          يحل محلها، فبيحصل ازدواج/إزاحة غلط. تثبيت الاتجاه هنا على "ltr"
          يخلي نقطة البداية الافتراضية ثابتة من الشمال دايمًا (بالظبط زي
          ما إحنا حاسبين في topLayout/handleTopLayoutChange)، بغض النظر عن
          لغة الصفحة — والمحتوى *جوه* كل عنصر بيرجّع اتجاهه الصحيح مرة
          تانية عن طريق dir={isRTL ? "rtl" : "ltr"} على كل div فردي تحت. */}
      <div dir="ltr">
        <ReactGridLayout
          key={isRTL ? "grid-rtl" : "grid-ltr"}
          className="layout"
          layout={topLayout}
          cols={GRID_COLS}
          rowHeight={ROW_HEIGHT}
          margin={[16, 16]}
          containerPadding={[0, 0]}
          isDraggable={editMode}
          isResizable={editMode}
          draggableCancel=".no-drag"
          compactType={editMode ? "vertical" : null}
          onLayoutChange={handleTopLayoutChange}
        >
          {topLevelItems.map((item) => (
            <div key={item.item_key} dir={isRTL ? "rtl" : "ltr"}>{renderItem(item)}</div>
          ))}
        </ReactGridLayout>
      </div>

      {/* Breakpoint tabs — only shown while customizing: each tab edits a
          fully independent saved arrangement for that screen size (see
          activeBreakpoint/editBreakpoint above). Outside customize mode the
          dashboard always renders whichever arrangement matches the real
          screen width automatically, so there's nothing to pick here. */}
      {editMode && (
        <div className="flex flex-wrap items-center gap-3 bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-3">
          <span className="text-xs text-gray-400 font-medium">{t("breakpoint_tabs_hint", "Choose a screen size to customize its own arrangement.")}</span>
          <div className="flex items-center gap-1.5">
            {BREAKPOINT_TABS.map(({ key, icon: Icon, labelKey, labelEn }) => (
              <button
                key={key}
                type="button"
                onClick={() => handleBreakpointTabChange(key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-colors ${
                  editBreakpoint === key ? "text-white" : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                }`}
                style={editBreakpoint === key ? { backgroundColor: themeColor } : undefined}
              >
                <Icon className="w-4 h-4" /> {t(labelKey, labelEn)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Customize toolbar — kept below everything so it doesn't shift as
          you scroll through the dashboard itself. Each user's arrangement
          is personal: it's saved per-user (and now per-breakpoint) and
          doesn't affect anyone else's dashboard. */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-3">
        <div className="flex items-center gap-2 text-xs text-gray-400 font-medium">
          <Squares2X2Icon className="w-4 h-4" />
          {editMode ? t("customize_hint", "Drag to move or resize from the corner.") : t("personal_hint", "This dashboard's layout is personal to you.")}
        </div>
        <div className="flex items-center gap-2">
          {editMode && (
            <>
              <button
                onClick={handleResetLayout}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gray-50 text-gray-600 text-xs font-bold hover:bg-gray-100 transition-colors"
              >
                <ArrowPathIcon className="w-4 h-4" /> {t("reset_button", "Reset to Default")}
              </button>
              <button
                onClick={handleSaveLayout}
                disabled={savingLayout}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-white text-xs font-bold shadow-lg transition-transform hover:scale-105 disabled:opacity-60"
                style={{ backgroundColor: themeColor }}
              >
                <CheckIcon className="w-4 h-4" /> {savingLayout ? t("saving_button", "Saving...") : t("save_button", "Save Layout")}
              </button>
            </>
          )}
          <button
            onClick={handleToggleEditMode}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-colors ${
              editMode ? "bg-gray-800 text-white" : "bg-gray-50 text-gray-600 hover:bg-gray-100"
            }`}
          >
            {editMode ? t("done_button", "Done") : t("customize_button", "Customize Layout")}
          </button>
        </div>
      </div>
    </div>
  );
}
